/**
 * Hooks de SendingIdentity (Fase 2 - Email Deliverability)
 *
 * Wrappea sendingIdentitiesService con React Query.
 * Convencion de query keys:
 *   ['sending-identities']                          -> lista
 *   ['sending-identities', { params }]              -> lista con filtros
 *   ['sending-identities', id]                      -> detalle
 *   ['sending-identities', id, 'health']            -> health endpoint
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/shared/contexts/AuthContext';
import {
  sendingIdentitiesService,
  type CustomizeIdentityInput,
  type DelegateSetupInput,
  type DestroyOptions,
  type PrecheckInput,
  type ProvisionCustomInput,
  type ProvisionDelegatedInput,
  type ProvisionFallbackInput,
} from '@/services/api/sendingIdentitiesService';

const KEY_BASE = ['sending-identities'] as const;

// =====================================================================
// Queries
// =====================================================================

export function useSendingIdentities(params?: Record<string, unknown>) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [...KEY_BASE, params ?? null],
    queryFn: () => sendingIdentitiesService.list(params),
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * useSendingIdentity
 *
 * Detalle de una SendingIdentity. La actualizacion en tiempo real cuando el
 * backend emite transiciones FSM (pending -> verifying -> verified/failed)
 * la maneja `useSendingIdentityWebSocket` que invalida esta query — NO
 * usamos polling.
 */
export function useSendingIdentity(id: string | undefined) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [...KEY_BASE, id],
    queryFn: () => sendingIdentitiesService.retrieve(id!),
    enabled: isAuthenticated && !!id,
    staleTime: 30 * 1000,
  });
}

export function useSendingIdentityHealth(id: string | undefined, enabled = true) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [...KEY_BASE, id, 'health'],
    queryFn: () => sendingIdentitiesService.health(id!),
    enabled: isAuthenticated && !!id && enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// =====================================================================
// Mutations
// =====================================================================

export function useProvisionFallback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProvisionFallbackInput) =>
      sendingIdentitiesService.provisionFallback(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_BASE });
    },
  });
}

export function useProvisionCustom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProvisionCustomInput) =>
      sendingIdentitiesService.provisionCustom(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_BASE });
    },
  });
}

export function useCheckIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sendingIdentitiesService.check(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: [...KEY_BASE, id] });
      qc.invalidateQueries({ queryKey: KEY_BASE });
    },
  });
}

export function useForceVerifiedIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      sendingIdentitiesService.forceVerified(id, reason),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: [...KEY_BASE, id] });
      qc.invalidateQueries({ queryKey: KEY_BASE });
    },
  });
}

export function useTestSendIdentity() {
  return useMutation({
    mutationFn: ({ id, toEmail }: { id: string; toEmail: string }) =>
      sendingIdentitiesService.testSend(id, toEmail),
  });
}

export function useDisableIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      sendingIdentitiesService.disable(id, reason),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: [...KEY_BASE, id] });
      qc.invalidateQueries({ queryKey: KEY_BASE });
    },
  });
}

export function useCustomizeIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CustomizeIdentityInput }) =>
      sendingIdentitiesService.customize(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: [...KEY_BASE, id] });
      qc.invalidateQueries({ queryKey: KEY_BASE });
    },
  });
}

export function useDestroyIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, options }: { id: string; options?: DestroyOptions }) =>
      sendingIdentitiesService.destroy(id, options),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_BASE });
    },
  });
}

/**
 * usePrecheckDomain
 *
 * Mutation que valida un dominio raiz para subdominio delegado: formato,
 * resolucion DNS, disponibilidad, deteccion del proveedor (Cloudflare, GoDaddy,
 * Namecheap, Route53, otro). NO crea nada en SES ni en Route53.
 *
 * Es mutation y no query porque se dispara explicitamente al click "Verificar"
 * y la respuesta no se cachea — cada nuevo dominio es una invocacion fresca.
 */
export function usePrecheckDomain() {
  return useMutation({
    mutationFn: (input: PrecheckInput) => sendingIdentitiesService.precheck(input),
  });
}

/**
 * useProvisionDelegated
 *
 * Crea hosted zone Route53 + identity SES para subdominio delegado. Idempotente:
 * si la company ya tiene una identity activa para el FQDN, devuelve la existente
 * con sus mismos nameservers (no recrea la hosted zone).
 */
export function useProvisionDelegated() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProvisionDelegatedInput) =>
      sendingIdentitiesService.provisionDelegated(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_BASE });
    },
  });
}

/**
 * useDelegateSetup
 *
 * Manda email al tecnico (IT del cliente) con un link unico para configurar
 * los NS records. El backend genera el token (7 dias TTL) y persiste los
 * delegate_* en la identity.
 */
export function useDelegateSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: DelegateSetupInput }) =>
      sendingIdentitiesService.delegate(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: [...KEY_BASE, id] });
    },
  });
}
